import React, { useState, useEffect } from 'react';
import { Package, Search, Download, RefreshCw, AlertTriangle, X, CheckCircle, List, Check, Printer } from 'lucide-react';
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  Button,
  Checkbox
} from '@mui/material';
import MainService from '../../services/MainService';
import * as XLSX from 'xlsx';
import { getApiBaseUrl } from '../../services/appSettings';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';

function AmountChip({ label, value, valueClass = '' }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50/90 px-2.5 py-2">
      <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`mt-0.5 font-mono text-sm font-semibold tabular-nums text-gray-900 ${valueClass}`}>{value}</p>
    </div>
  );
}

const ShipmentApprovals = () => {
  const { language } = useLanguage();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredShipments, setFilteredShipments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedShipments, setSelectedShipments] = useState(new Set());

  // Summary totals for selected shipments
  const [summaryTotals, setSummaryTotals] = useState({
    TotalExpAmt: 0,
    TotalCashInHand: 0,
    TotalExpense: 0,
    TotalIncome: 0,
    TotalVAT: 0,
    TotalAdvance: 0,
    TotalBalanceIncome: 0,
    TotalMoneyFromClient: 0,
    TotalClientMoney: 0
  });

  useEffect(() => {
    loadShipments();
  }, []);

  useEffect(() => {
    handleSearch();
  }, [searchTerm, shipments]);

  useEffect(() => {
    calculateTotals();
  }, [selectedShipments, filteredShipments]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadShipments = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.ShipmentsForApprovals();
      if (response && Array.isArray(response)) {
        const shipmentsWithChecked = response.map(s => ({ ...s, checked: false }));
        setShipments(shipmentsWithChecked);
        setFilteredShipments(shipmentsWithChecked);
      } else {
        setShipments([]);
        setFilteredShipments([]);
      }
    } catch (err) {
      console.error('Error loading shipments:', err);
      setError('Failed to load shipments');
      setShipments([]);
      setFilteredShipments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setFilteredShipments(shipments);
      setCurrentPage(1);
      return;
    }

    const keyword = searchTerm.toLowerCase();
    const filtered = shipments.filter(shipment => 
      (shipment.RefNo && shipment.RefNo.toLowerCase().includes(keyword)) ||
      (shipment.DeliveryNote?.RefNo && shipment.DeliveryNote.RefNo.toLowerCase().includes(keyword)) ||
      (shipment.DeliveryNote?.DriverName && shipment.DeliveryNote.DriverName.toLowerCase().includes(keyword)) ||
      (shipment.CreatedBy?.UserName && shipment.CreatedBy.UserName.toLowerCase().includes(keyword))
    );
    setFilteredShipments(filtered);
    setCurrentPage(1);
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    const pageIds = paginatedShipments.map((s) => s.Id);
    if (checked) {
      setSelectedShipments((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.add(id));
        return next;
      });
      setFilteredShipments((prev) =>
        prev.map((s) => (pageIds.includes(s.Id) ? { ...s, checked: true } : s))
      );
    } else {
      setSelectedShipments((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.delete(id));
        return next;
      });
      setFilteredShipments((prev) =>
        prev.map((s) => (pageIds.includes(s.Id) ? { ...s, checked: false } : s))
      );
    }
  };

  const handleSelectShipment = (shipmentId, checked) => {
    setSelectedShipments(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(shipmentId);
      } else {
        newSet.delete(shipmentId);
        setSelectAll(false);
      }
      return newSet;
    });

    setFilteredShipments(prev => prev.map(s => 
      s.Id === shipmentId ? { ...s, checked } : s
    ));
  };

  const calculateTotals = () => {
    const selected = filteredShipments.filter(s => selectedShipments.has(s.Id));
    
    const totals = {
      TotalExpAmt: 0,
      TotalCashInHand: 0,
      TotalExpense: 0,
      TotalIncome: 0,
      TotalVAT: 0,
      TotalAdvance: 0,
      TotalBalanceIncome: 0,
      TotalMoneyFromClient: 0,
      TotalClientMoney: 0
    };

    selected.forEach(shipment => {
      totals.TotalExpAmt += parseFloat(shipment.ExpectedAmount || 0);
      totals.TotalCashInHand += parseFloat(shipment.CashInHand || 0);
      totals.TotalExpense += parseFloat(shipment.TotalExpense || 0);
      totals.TotalIncome += parseFloat(shipment.TotalIncome || 0);
      totals.TotalVAT += parseFloat(shipment.TotalVat || 0);
      totals.TotalAdvance += parseFloat(shipment.TotalAdvance || 0);
      totals.TotalBalanceIncome += parseFloat(shipment.BalanceIncome || 0);
      totals.TotalMoneyFromClient += parseFloat(shipment.TotalMoneyFromClient || 0);
      totals.TotalClientMoney += parseFloat(shipment.TotalClientMoney || 0);
    });

    setSummaryTotals(totals);
  };

  const handleApproveShipments = async () => {
    if (selectedShipments.size === 0) {
      setError('Please select at least one shipment to approve');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const shipmentArray = Array.from(selectedShipments).map(id => ({ Id: id }));
      const response = await MainService.ApproveShipment(shipmentArray);
      
      if (response === 'Success' || response === true) {
        setSuccess(`Successfully approved ${selectedShipments.size} shipment(s)`);
        setSelectedShipments(new Set());
        setSelectAll(false);
        loadShipments();
      } else {
        setError(response || 'Failed to approve shipments');
      }
    } catch (err) {
      console.error('Error approving shipments:', err);
      setError('Failed to approve shipments');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintShipment = async (id) => {
    try {
      setLoading(true);
      const response = await MainService.PrintShipment(id);
      if (response) {
        window.open(`${getApiBaseUrl()}ReportFiles/${response}`, '_blank');
        setSuccess('Opening print preview...');
      } else {
        setError('Print error');
      }
    } catch (err) {
      console.error('Error printing shipment:', err);
      setError('Print error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '0.00';
    return parseFloat(amount).toFixed(2);
  };

  const exportToExcel = () => {
    if (filteredShipments.length === 0) {
      setError('No data to export');
      return;
    }

    const dataToExport = filteredShipments.map(shipment => ({
      'Ref No': shipment.RefNo || '',
      'Delivery Note': shipment.DeliveryNote?.RefNo || '',
      'Driver': shipment.DeliveryNote?.DriverName || '',
      'Date': formatDate(shipment.Date),
      'Created By': shipment.CreatedBy?.UserName || '',
      'Expected Amount': formatCurrency(shipment.ExpectedAmount),
      'Total Expense': formatCurrency(shipment.TotalExpense),
      'Cash In Hand': formatCurrency(shipment.CashInHand),
      'Total Income': formatCurrency(shipment.TotalIncome),
      'Total VAT': formatCurrency(shipment.TotalVat),
      'Total Advance': formatCurrency(shipment.TotalAdvance),
      'Balance Income': formatCurrency(shipment.BalanceIncome),
      'Total Money From Client': formatCurrency(shipment.TotalMoneyFromClient),
      'Total Client Money': formatCurrency(shipment.TotalClientMoney)
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Shipments');
    XLSX.writeFile(wb, `ShipmentApproval_${new Date().toISOString().split('T')[0]}.xlsx`);
    setSuccess('Data exported to Excel successfully');
  };

  const paginatedShipments = filteredShipments.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const totalPages = Math.ceil(filteredShipments.length / pageSize);

  // Calculate footer totals for all displayed shipments
  const footerTotals = {
    ExpectedAmount: filteredShipments.reduce((sum, s) => sum + parseFloat(s.ExpectedAmount || 0), 0),
    TotalExpense: filteredShipments.reduce((sum, s) => sum + parseFloat(s.TotalExpense || 0), 0),
    CashInHand: filteredShipments.reduce((sum, s) => sum + parseFloat(s.CashInHand || 0), 0),
    TotalIncome: filteredShipments.reduce((sum, s) => sum + parseFloat(s.TotalIncome || 0), 0),
    TotalVat: filteredShipments.reduce((sum, s) => sum + parseFloat(s.TotalVat || 0), 0),
    TotalAdvance: filteredShipments.reduce((sum, s) => sum + parseFloat(s.TotalAdvance || 0), 0),
    BalanceIncome: filteredShipments.reduce((sum, s) => sum + parseFloat(s.BalanceIncome || 0), 0),
    TotalMoneyFromClient: filteredShipments.reduce((sum, s) => sum + parseFloat(s.TotalMoneyFromClient || 0), 0),
    TotalClientMoney: filteredShipments.reduce((sum, s) => sum + parseFloat(s.TotalClientMoney || 0), 0)
  };

  // Check if all current page items are selected
  useEffect(() => {
    if (paginatedShipments.length > 0) {
      const allSelected = paginatedShipments.every(s => selectedShipments.has(s.Id));
      setSelectAll(allSelected);
    } else {
      setSelectAll(false);
    }
  }, [selectedShipments, paginatedShipments]);

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-6">
        <div className="mb-5 flex flex-col gap-4 border-b border-gray-200 pb-5 md:flex-row md:items-start md:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <List className="mt-0.5 h-7 w-7 shrink-0 text-[#46955A]" />
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight text-gray-900">{getPageHeading(language, 'shipmentApprovals')}</h1>
              <p className="mt-1 text-sm text-gray-500">Review amounts and approve pending shipments.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 md:gap-3">
            <TextField
              size="small"
              placeholder="Ref no, delivery note, driver…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search className="h-4 w-4 text-gray-400" />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 220,
                flex: '1 1 220px',
                maxWidth: { md: 320 },
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#46955A',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#46955A',
                  },
                },
              }}
            />
            <Tooltip title="Export to Excel">
              <IconButton
                onClick={exportToExcel}
                disabled={filteredShipments.length === 0}
                sx={{
                  color: '#46955A',
                  '&:hover': { backgroundColor: '#f0f9f4' },
                  '&:disabled': { color: '#9CA3AF' }
                }}
              >
                <Download className="w-5 h-5" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton
                onClick={loadShipments}
                disabled={loading}
                sx={{
                  color: '#46955A',
                  '&:hover': { backgroundColor: '#f0f9f4' }
                }}
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        {/* Shipment cards */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={32} sx={{ color: '#46955A' }} />
              <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>Loading shipments...</span>
            </Box>
          </Box>
        ) : (
          <>
            {paginatedShipments.length > 0 ? (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2.5">
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700">
                    <Checkbox
                      checked={selectAll}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      size="small"
                      sx={{
                        color: '#46955A',
                        padding: 0,
                        '&.Mui-checked': { color: '#46955A' },
                      }}
                    />
                    Select all on this page
                  </label>
                  <span className="text-xs text-gray-500">
                    {paginatedShipments.length} card{paginatedShipments.length !== 1 ? 's' : ''} on this page
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {paginatedShipments.map((shipment, index) => {
                    const rowNum = (currentPage - 1) * pageSize + index + 1;
                    const isSelected = selectedShipments.has(shipment.Id);
                    return (
                      <article
                        key={shipment.Id}
                        className={`rounded-xl border bg-white p-4 shadow-sm transition-all ${
                          isSelected
                            ? 'border-[#46955A] ring-2 ring-[#46955A]/20 bg-[#f9fdf9]'
                            : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3">
                          <div className="flex min-w-0 flex-1 items-start gap-3">
                            <Checkbox
                              checked={isSelected}
                              onChange={(e) => handleSelectShipment(shipment.Id, e.target.checked)}
                              size="small"
                              sx={{
                                color: '#46955A',
                                padding: 0,
                                mt: 0.5,
                                '&.Mui-checked': { color: '#46955A' },
                              }}
                            />
                            <span className="mt-1 shrink-0 text-xs tabular-nums text-gray-400">#{rowNum}</span>
                            <div className="min-w-0 flex-1">
                              <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-gray-500">Ref no</p>
                              <p className="truncate text-lg font-bold tracking-tight text-gray-900">
                                {shipment.RefNo || '—'}
                              </p>
                              <p className="mt-1 text-xs text-gray-500">Date · {formatDate(shipment.Date)}</p>
                            </div>
                          </div>
                          <Tooltip title="Print shipment">
                            <IconButton
                              onClick={() => handlePrintShipment(shipment.Id)}
                              size="small"
                              sx={{ color: '#46955A', '&:hover': { backgroundColor: '#f0f9f4' } }}
                            >
                              <Printer className="h-5 w-5" />
                            </IconButton>
                          </Tooltip>
                        </div>

                        <dl className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
                          <div className="min-w-0 sm:col-span-1">
                            <dt className="text-xs font-medium text-gray-500">Delivery note</dt>
                            <dd className="truncate font-medium text-gray-900" title={shipment.DeliveryNote?.RefNo || ''}>
                              {shipment.DeliveryNote?.RefNo || '—'}
                            </dd>
                          </div>
                          <div className="min-w-0">
                            <dt className="text-xs font-medium text-gray-500">Driver</dt>
                            <dd className="truncate text-gray-800" title={shipment.DeliveryNote?.DriverName || ''}>
                              {shipment.DeliveryNote?.DriverName || '—'}
                            </dd>
                          </div>
                          <div className="min-w-0">
                            <dt className="text-xs font-medium text-gray-500">Created by</dt>
                            <dd className="truncate text-gray-800" title={shipment.CreatedBy?.UserName || ''}>
                              {shipment.CreatedBy?.UserName || '—'}
                            </dd>
                          </div>
                        </dl>

                        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                          <AmountChip label="Expected" value={formatCurrency(shipment.ExpectedAmount)} />
                          <AmountChip label="Expense" value={formatCurrency(shipment.TotalExpense)} valueClass="text-red-700" />
                          <AmountChip label="Cash" value={formatCurrency(shipment.CashInHand)} />
                          <AmountChip label="Income" value={formatCurrency(shipment.TotalIncome)} valueClass="text-emerald-700" />
                          <AmountChip label="VAT" value={formatCurrency(shipment.TotalVat)} />
                          <AmountChip label="Advance" value={formatCurrency(shipment.TotalAdvance)} />
                          <AmountChip label="Balance" value={formatCurrency(shipment.BalanceIncome)} valueClass="text-[#46955A]" />
                          <AmountChip label="From client" value={formatCurrency(shipment.TotalMoneyFromClient)} />
                          <AmountChip label="Client total" value={formatCurrency(shipment.TotalClientMoney)} />
                        </div>
                      </article>
                    );
                  })}
                </div>

                {/* Filtered list totals */}
                <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gradient-to-br from-gray-50 to-white p-4">
                  <p className="mb-3 text-center text-xs font-bold uppercase tracking-wider text-gray-500">
                    Total for filtered list ({filteredShipments.length} shipment{filteredShipments.length !== 1 ? 's' : ''})
                  </p>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                    <AmountChip label="Expected" value={formatCurrency(footerTotals.ExpectedAmount)} />
                    <AmountChip label="Expense" value={formatCurrency(footerTotals.TotalExpense)} valueClass="text-red-700" />
                    <AmountChip label="Cash" value={formatCurrency(footerTotals.CashInHand)} />
                    <AmountChip label="Income" value={formatCurrency(footerTotals.TotalIncome)} valueClass="text-emerald-700" />
                    <AmountChip label="VAT" value={formatCurrency(footerTotals.TotalVat)} />
                    <AmountChip label="Advance" value={formatCurrency(footerTotals.TotalAdvance)} />
                    <AmountChip label="Balance" value={formatCurrency(footerTotals.BalanceIncome)} valueClass="text-[#46955A]" />
                    <AmountChip label="From client" value={formatCurrency(footerTotals.TotalMoneyFromClient)} />
                    <AmountChip label="Client total" value={formatCurrency(footerTotals.TotalClientMoney)} />
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-gray-50/50 py-16 text-gray-400">
                <Package className="mb-3 h-12 w-12 opacity-50" />
                <p className="text-sm font-medium text-gray-500">
                  {searchTerm ? 'No shipments found matching your search' : 'No shipments pending approval'}
                </p>
              </div>
            )}

            {/* Selected shipments summary — totals for checked rows */}
            {selectedShipments.size > 0 && (
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm ring-1 ring-gray-900/5">
                <div className="flex flex-col gap-3 bg-gradient-to-r from-[#2d6a3f] to-[#46955A] px-4 py-3.5 text-white sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/15">
                      <Package className="h-5 w-5 opacity-95" strokeWidth={2} />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-base font-bold tracking-tight">Selected shipments summary</h2>
                      <p className="mt-0.5 text-xs text-white/85">
                        Aggregated amounts for all checked rows (all pages in the current filter).
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 sm:pl-4">
                    <span className="rounded-full bg-white/20 px-3 py-1.5 text-center text-xs font-semibold uppercase tracking-wide text-white">
                      {selectedShipments.size} selected
                    </span>
                  </div>
                </div>
                <div className="max-h-[min(52vh,420px)] overflow-y-auto overscroll-contain">
                  <ul className="divide-y divide-gray-100">
                    {[
                      { label: 'Expected amount', value: summaryTotals.TotalExpAmt, hint: 'Sum of expected' },
                      { label: 'Cash in hand', value: summaryTotals.TotalCashInHand, hint: null },
                      { label: 'Total expense', value: summaryTotals.TotalExpense, hint: null, valueClass: 'text-red-700' },
                      { label: 'Total income', value: summaryTotals.TotalIncome, hint: null, valueClass: 'text-emerald-700' },
                      { label: 'Total VAT', value: summaryTotals.TotalVAT, hint: null },
                      { label: 'Total advance', value: summaryTotals.TotalAdvance, hint: null },
                      { label: 'Balance income', value: summaryTotals.TotalBalanceIncome, hint: null, valueClass: 'text-[#46955A]' },
                      { label: 'Total money from client', value: summaryTotals.TotalMoneyFromClient, hint: null },
                      { label: 'Total client money', value: summaryTotals.TotalClientMoney, hint: null },
                    ].map((item, idx) => (
                      <li
                        key={item.label}
                        className={`flex items-center justify-between gap-4 px-4 py-3.5 sm:px-5 ${
                          idx % 2 === 1 ? 'bg-gray-50/70' : 'bg-white'
                        }`}
                      >
                        <div className="min-w-0 pr-2">
                          <p className="text-sm font-medium text-gray-900">{item.label}</p>
                          {item.hint ? <p className="mt-0.5 text-xs text-gray-500">{item.hint}</p> : null}
                        </div>
                        <span
                          className={`shrink-0 font-mono text-sm font-semibold tabular-nums ${
                            item.valueClass || 'text-gray-900'
                          }`}
                        >
                          {formatCurrency(item.value)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-center gap-3 mt-4">
              <Button
                variant="outlined"
                startIcon={<RefreshCw className="w-4 h-4" />}
                onClick={loadShipments}
                disabled={loading}
                sx={{
                  borderColor: '#46955A',
                  color: '#46955A',
                  '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' },
                  '&:disabled': { borderColor: '#9CA3AF', color: '#9CA3AF' }
                }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Check className="w-4 h-4" />}
                onClick={handleApproveShipments}
                disabled={loading || selectedShipments.size === 0}
                sx={{
                  backgroundColor: '#46955A',
                  '&:hover': { backgroundColor: '#3d7d4f' },
                  '&:disabled': { backgroundColor: '#9CA3AF' }
                }}
              >
                Approve Shipments ({selectedShipments.size})
              </Button>
            </div>

            {/* Pagination Footer */}
            {filteredShipments.length > 0 && (
              <div className="flex items-center justify-between mt-4 px-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, filteredShipments.length)} of {filteredShipments.length} shipments
                  </span>
                  <FormControl size="small" sx={{ minWidth: 80 }}>
                    <Select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(e.target.value);
                        setCurrentPage(1);
                      }}
                      sx={{
                        fontSize: '0.875rem',
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#46955A',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#46955A',
                          },
                        },
                      }}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>
                </div>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(e, value) => setCurrentPage(value)}
                  color="primary"
                  sx={{
                    '& .MuiPaginationItem-root.Mui-selected': {
                      backgroundColor: '#46955A',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#3d7d4f',
                      },
                    },
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ShipmentApprovals;
